import { siloMarkets, siloV2LenderKey } from '@1delta/data-sdk'
import { createMarketUid } from '../../../utils'
import type {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'

/**
 * ISiloOracle ABI fragments for `quote` and `quoteToken`.
 *
 * `quote(uint256 _baseAmount, address _baseToken)` returns the value of
 * `_baseAmount` of `_baseToken` denominated in the oracle's quote token.
 *
 * `quoteToken()` returns the address of the denomination token.
 */
const SiloOracleAbi = [
  {
    inputs: [
      { name: '_baseAmount', type: 'uint256' },
      { name: '_baseToken', type: 'address' },
    ],
    name: 'quote',
    outputs: [{ name: 'quoteAmount', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'quoteToken',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

/**
 * Metadata carried alongside the multicall batch so the parser can
 * reassemble results without re-reading on-chain state.
 */
interface SiloV2OracleMeta {
  /** Entries to process, one per silo half */
  entries: {
    token: string
    decimals: number
    silo: string
    lenderKey: string
    oracle: string
    /** Other half's token — used for 1:1 fallback when this side is static */
    partnerToken: string
    /**
     * True when this side has a zero-address oracle (constant price = 1.0).
     * Treated like a Morpho loan asset: priced via the partner token's USD
     * (or its own, if independently known) and flagged with `staticBase`.
     */
    isStatic: boolean
  }[]
  /**
   * Dedup map: "oracleLc:tokenLc" → index into the calls array.
   * The same oracle+token pair only needs one `quote` call even if it
   * appears in multiple markets.
   */
  callIndexMap: { [key: string]: number }
  /**
   * "oracleLc" → index of the quoteToken() call for that oracle.
   */
  quoteTokenIndexMap: { [key: string]: number }
}

/** Resolve USD price for an asset using assetGroup → bare-address fallback. */
function lookupUSD(context: ParseContext, asset: string): number {
  const lc = asset.toLowerCase()
  const groupKey =
    context.tokenList?.[lc]?.assetGroup ?? `${context.chainId}-${lc}`
  return context.usdPrices[groupKey] ?? context.usdPrices[lc] ?? 0
}

/**
 * Build multicall calls for all Silo V2 oracle prices on the given chain.
 *
 * Three cases per pair:
 *   1. Both halves have a real oracle → standard `quote` + `quoteToken` calls.
 *   2. Exactly one half is zero-address (constant = 1.0) → emit that side as
 *      `staticBase` (priced via the partner token's USD) and run the normal
 *      flow on the other side. The non-static oracle is expected to quote in
 *      the static side's token, so the existing `quote * qtUSD` math just
 *      works once the partner USD price is in `usdPrices`.
 *   3. Both halves are zero-address → the pair is 1:1 in its own terms. Only
 *      emit if at least one of the two tokens has a real oracle elsewhere on
 *      the chain (e.g. WBTC priced by another silo); otherwise (the WBTC-LBTC
 *      case where neither has a non-constant feed) skip the pair entirely.
 *
 * Calls are deduplicated at the (oracle, token) level for `quote` and at
 * the oracle level for `quoteToken`.
 */
export function getSiloV2Calls(
  chainId: string,
): FetcherResult<SiloV2OracleMeta>[] {
  const allMarkets = siloMarkets()?.[chainId]
  if (!allMarkets || allMarkets.length === 0) return []

  // Pass 1: tokens that have at least one non-zero solvencyOracle anywhere on
  // this chain. Used to decide whether a both-static pair is salvageable.
  const tokensWithRealOracle = new Set<string>()
  for (const market of allMarkets) {
    for (const half of [market.silo0, market.silo1]) {
      if (half.solvencyOracle && half.solvencyOracle !== ZERO_ADDRESS) {
        tokensWithRealOracle.add(half.token.toLowerCase())
      }
    }
  }

  const calls: MulticallCall[] = []
  const callIndexMap: { [key: string]: number } = {}
  const quoteTokenIndexMap: { [key: string]: number } = {}
  const entries: SiloV2OracleMeta['entries'] = []

  for (const market of allMarkets) {
    const lenderKey = siloV2LenderKey(market.siloConfig)
    const halves = [market.silo0, market.silo1] as const
    const isStatic = halves.map(
      (h) => !h.solvencyOracle || h.solvencyOracle === ZERO_ADDRESS,
    )

    // Case 3: both static. Skip unless one side can be priced cross-silo.
    if (isStatic[0] && isStatic[1]) {
      const t0 = halves[0].token.toLowerCase()
      const t1 = halves[1].token.toLowerCase()
      if (!tokensWithRealOracle.has(t0) && !tokensWithRealOracle.has(t1)) {
        continue
      }
    }

    for (let i = 0; i < 2; i++) {
      const half = halves[i]
      const partner = halves[1 - i]
      const tokenLc = half.token.toLowerCase()
      const partnerLc = partner.token.toLowerCase()

      if (isStatic[i]) {
        // Static side: no on-chain call. Parser will resolve USD via partner.
        entries.push({
          token: tokenLc,
          decimals: half.decimals,
          silo: half.silo.toLowerCase(),
          lenderKey,
          oracle: ZERO_ADDRESS,
          partnerToken: partnerLc,
          isStatic: true,
        })
        continue
      }

      const oracleLc = half.solvencyOracle.toLowerCase()

      entries.push({
        token: tokenLc,
        decimals: half.decimals,
        silo: half.silo.toLowerCase(),
        lenderKey,
        oracle: oracleLc,
        partnerToken: partnerLc,
        isStatic: false,
      })

      // Deduplicate quote(amount, token) per (oracle, token)
      const quoteKey = `${oracleLc}:${tokenLc}`
      if (!(quoteKey in callIndexMap)) {
        callIndexMap[quoteKey] = calls.length
        calls.push({
          address: half.solvencyOracle,
          name: 'quote',
          params: [BigInt(10) ** BigInt(half.decimals), half.token],
        })
      }

      // Deduplicate quoteToken() per oracle
      if (!(oracleLc in quoteTokenIndexMap)) {
        quoteTokenIndexMap[oracleLc] = calls.length
        calls.push({
          address: half.solvencyOracle,
          name: 'quoteToken',
          params: [],
        })
      }
    }
  }

  if (entries.length === 0) return []

  return [
    {
      calls,
      meta: { entries, callIndexMap, quoteTokenIndexMap },
      lender: 'SILO_V2',
    },
  ]
}

/**
 * Parse Silo V2 oracle multicall results into price entries.
 *
 * Static halves (zero-address oracle) are resolved by looking up the USD
 * price of the side's own token, falling back to the partner token (the
 * relationship is 1:1 by definition). They are flagged with `staticBase`
 * + `baseAsset = self`, mirroring the Morpho loan-asset entries.
 *
 * Non-static halves use `quote(1.0, token) → priceInQuoteToken × qtUSD`.
 * When the static side's token IS the partner oracle's quoteToken, the
 * non-static math automatically picks up the right USD via the shared
 * `usdPrices` map (populated by other lenders earlier in the parse order).
 */
export function parseSiloV2Results(
  data: any[],
  meta: SiloV2OracleMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const entries: OraclePriceEntry[] = []
  const seen = new Set<string>()

  for (const entry of meta.entries) {
    // Dedup output: same (lenderKey, token) only emitted once
    const dedupKey = `${entry.lenderKey}:${entry.token}`
    if (seen.has(dedupKey)) continue

    try {
      let priceUSD = 0
      let rawPrice = 1

      if (entry.isStatic) {
        const ownUSD = lookupUSD(context, entry.token)
        const partnerUSD = lookupUSD(context, entry.partnerToken)
        priceUSD = ownUSD || partnerUSD
        if (!priceUSD) continue
        rawPrice = 1
      } else {
        // 1. Get raw quote amount
        const quoteCallIdx =
          meta.callIndexMap[`${entry.oracle}:${entry.token}`]
        if (quoteCallIdx === undefined) continue
        const rawQuote = data[quoteCallIdx]
        if (!rawQuote || rawQuote === '0x') continue

        const quoteAmount = BigInt(rawQuote.toString())
        if (quoteAmount === 0n) continue

        // 2. Get quote token address
        const qtIdx = meta.quoteTokenIndexMap[entry.oracle]
        if (qtIdx === undefined) continue
        const rawQt = data[qtIdx]
        if (!rawQt || rawQt === '0x') continue

        const quoteTokenAddr = (rawQt as string).toLowerCase()

        // Silo oracles return WAD-normalized quote values: for a base
        // amount of 10^baseDecimals (one whole base token), the result is
        // priceRatio × 1e18, regardless of the quote token's actual
        // decimals. Confirmed against both stable (siUSD/USDC) and PT
        // (PT-reUSD/USDC) oracles. Dividing by `qtDecimals` here would
        // overscale by 10^(18 - qtDecimals) — for USDC quote tokens that's
        // a 1e12 factor, which surfaces as e.g. $9.4e11 for a $0.94 asset.
        const priceInQt = Number(quoteAmount) / 1e18
        if (isNaN(priceInQt) || priceInQt === 0) continue

        // 5. Convert to USD via the partner / quoteToken's USD price
        const qtUSD = lookupUSD(context, quoteTokenAddr)
        if (!qtUSD) continue

        rawPrice = priceInQt
        priceUSD = priceInQt * qtUSD
      }

      if (isNaN(priceUSD) || priceUSD === 0) continue

      entries.push({
        asset: entry.token,
        price: rawPrice,
        priceUSD,
        marketUid: createMarketUid(
          context.chainId,
          entry.lenderKey,
          entry.silo,
        ),
        targetLender: entry.lenderKey,
        ...(entry.isStatic
          ? { staticBase: true, baseAsset: entry.token }
          : {}),
      })
      seen.add(dedupKey)
    } catch {
      // Skip invalid results
    }
  }

  return entries
}

/**
 * ABI fragments needed for Silo V2 oracle calls
 */
export function getSiloV2Abi() {
  return SiloOracleAbi
}

/**
 * Silo V2 Oracle Fetcher module
 */
export const siloV2Fetcher = {
  getCalls: getSiloV2Calls,
  parse: parseSiloV2Results,
  getAbi: getSiloV2Abi,
}
